import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, UserIcon, MapPinIcon, LoaderIcon, AlertCircleIcon, PhoneIcon } from 'lucide-react';
interface FCIQuoteFormProps {
  initialZip: string;
  serviceSlug: string;
  onClose: () => void;
}
export function FCIQuoteForm({
  initialZip,
  serviceSlug,
  onClose
}: FCIQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [geoError, setGeoError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const getServiceLabel = (slug: string): string => {
    switch (slug) {
      case 'carpet':
        return 'Carpet';
      case 'laminate':
        return 'Laminate';
      case 'vinyl':
        return 'Vinyl';
      case 'stone':
        return 'Stone';
      case 'tile':
        return 'Tile';
      case 'wood-floors':
        return 'Wood Floors';
      default:
        return 'Flooring';
    }
  };
  const [formData, setFormData] = useState({
    BDMLeadType: 'test',
    oid: 'FCI_OID',
    alid: 'FCI_ALID',
    LeadID: 'FCI_LEADID',
    FirstName: '',
    LastName: '',
    Address: '',
    City: '',
    State: '',
    Zip: initialZip || '',
    Email: '',
    PhoneNumber: '',
    Campaign: 'FCI_CAMPAIGN',
    Code: '35',
    ServiceType: getServiceLabel(serviceSlug),
    TrustedFormCert: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.Zip;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      setGeoError('');
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          const stateAbbr = place['state abbreviation'];
          setFormData(prev => ({
            ...prev,
            City: place['place name'] || '',
            State: stateAbbr || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.Zip]);
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      PhoneNumber: digits
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            TrustedFormCert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {
        // continue polling
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const totalSteps = 2; // Address, Contact Info
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const validateAddress = (address: string): boolean => {
    const addressPattern = /^\d+\s+\S{2,}/;
    return addressPattern.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'Address') {
      setAddressError('');
    }
  };
  const handleAddressBlur = () => {
    if (formData.Address && !validateAddress(formData.Address)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.TrustedFormCert;
    const payload = {
      ...formData,
      TrustedFormCert: finalCert
    };
    // Submit to BDM lead management
    const bdmParams = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) bdmParams.append(key, String(value));
    });
    const bdmUrl = `http://bdmleadmanagement.valid1.net/Leads.aspx?${bdmParams.toString()}`;
    console.log('Submitting to FCI BDM:', bdmUrl);
    try {
      await fetch(bdmUrl, {
        method: 'POST',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('BDM post failed, continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = ({
    hideExit = false
  }: {
    hideExit?: boolean;
  }) => <header className="bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center sticky top-0 z-10" data-id="element-1446">
      <div className="flex flex-col" data-id="element-1447">
        <div className="flex items-baseline gap-2 mb-1" data-id="element-1448">
          <img src="https://cdn.magicpatterns.com/uploads/5wpLpVezYeAkEh7NG2YmbK/FCI_Logo.webp" alt="Floor Coverings International" className="h-8 md:h-10 object-contain" data-id="element-1449" />
        </div>
        <span className="text-[10px] md:text-xs font-semibold text-gray-600" data-id="element-1450">
          {getServiceLabel(serviceSlug)} Estimate
        </span>
      </div>
      <div className="flex items-center gap-6" data-id="element-1451">
        {!hideExit && <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-[#8B7D6B] transition-colors flex items-center gap-1" data-id="element-1452">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1453" />
            <span className="hidden sm:inline" data-id="element-1454">Exit</span>
          </button>}
      </div>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-white flex flex-col font-sans" data-id="element-1455">
        <Header hideExit data-id="element-1456" />

        {/* Hero Banner */}
        <div className="relative w-full h-64 md:h-80 flex items-center" data-id="element-1457">
          <div className="absolute inset-0 z-0" data-id="element-1458">
            <img src="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Flooring professional" className="w-full h-full object-cover" data-id="element-1459" />
          </div>
          <div className="absolute inset-0 z-10" data-id="element-1460">
            <div className="absolute left-0 top-0 bottom-0 w-full md:w-2/3 bg-[#8B7D6B]/90 flex flex-col justify-center px-8 md:px-16" data-id="element-1461">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-1462">
                Your Quote Request Has
                <br data-id="element-1463" />
                Been Submitted!
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-1464">
                An FCI representative will be calling you shortly.
              </p>
            </div>
          </div>
        </div>

        {/* Keep Your Phone Nearby Section */}
        <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center" data-id="element-1465">
          <div className="flex justify-center mb-6" data-id="element-1466">
            <div className="w-20 h-20 rounded-full bg-stone-50 flex items-center justify-center" data-id="element-1467">
              <PhoneIcon className="w-10 h-10 text-[#8B7D6B]" data-id="element-1468" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-1469">
            Keep your phone nearby!
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1470">
            An FCI representative will be reaching out to you at{' '}
            <span className="font-semibold text-gray-900" data-id="element-1471">
              {formData.PhoneNumber}
            </span>{' '}
            to discuss your {getServiceLabel(serviceSlug).toLowerCase()} project
            and schedule your free in-home estimate. The call typically comes
            within one business day.
          </p>
        </div>

        {/* Homeowner Info */}
        <div className="max-w-4xl mx-auto w-full px-6 py-12 text-center border-b border-gray-200" data-id="element-1472">
          <h2 className="text-[#8B7D6B] font-bold text-sm uppercase tracking-wider mb-6" data-id="element-1473">
            Your Submission Details
          </h2>
          <div className="text-gray-900 text-lg mb-2" data-id="element-1474">
            <p className="font-semibold text-xl" data-id="element-1475">
              {formData.FirstName} {formData.LastName}
            </p>
            <p data-id="element-1476">{formData.Address}</p>
            <p data-id="element-1477">
              {formData.City}, {formData.State} {formData.Zip}
            </p>
            <p data-id="element-1478">{formData.PhoneNumber}</p>
            <p className="text-gray-500" data-id="element-1479">{formData.Email}</p>
          </div>

          <div className="mt-6 inline-flex flex-col gap-2 text-sm text-gray-600" data-id="element-1480">
            <div className="flex items-center justify-center gap-2" data-id="element-1481">
              <span className="font-semibold text-gray-800" data-id="element-1482">
                Flooring Type:
              </span>
              <span data-id="element-1483">{getServiceLabel(serviceSlug)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-[#5C5145] py-16 mt-auto" data-id="element-1484">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1485">
            <p className="text-white/80 text-sm" data-id="element-1486">
              &copy; {new Date().getFullYear()} findservicequotes.io. All Rights
              Reserved.
            </p>
          </div>
        </footer>
      </div>;
  }
  return <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-id="element-1487">
      <Header data-id="element-1488" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-1489" />

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2" data-id="element-1490">
        <motion.div className="bg-[#8B7D6B] h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-1491" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-1492">
        <div className="w-full max-w-2xl" data-id="element-1493">
          <div className="mb-6 h-8" data-id="element-1494">
            {step > 1 && <button onClick={handleBack} className="text-gray-500 hover:text-[#8B7D6B] font-medium flex items-center gap-2 transition-colors" data-id="element-1495">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1496" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100" data-id="element-1497">
            <AnimatePresence mode="wait" custom={direction} data-id="element-1498">
              {/* STEP 1: Address */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1499">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#8B7D6B] mb-8 text-center" data-id="element-1500">
                    Where is your project located?
                  </h1>

                  <div className="space-y-5" data-id="element-1501">
                    <div data-id="element-1502">
                      <label htmlFor="Address" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1503">
                        Street Address
                      </label>
                      <input type="text" id="Address" name="Address" value={formData.Address} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#8B7D6B] focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} placeholder="123 Main St" data-id="element-1504" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-1505">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex flex-col gap-2" data-id="element-1506">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-1507">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1508" />
                            <span data-id="element-1509">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-1510">
                            <MapPinIcon className="w-4 h-4 text-[#8B7D6B] flex-shrink-0" data-id="element-1511" />
                            <input type="text" value={formData.Zip} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          Zip: val,
                          City: '',
                          State: ''
                        }));
                        setZipLookupDone(false);
                        setGeoError('');
                      }} onBlur={() => {
                        if (formData.Zip.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.Zip.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-stone-300 rounded-lg text-sm font-semibold text-[#8B7D6B] focus:outline-none focus:ring-2 focus:ring-[#8B7D6B] focus:border-transparent" data-id="element-1512" />
                          </div> : zipLookupDone && formData.City && formData.State ? <button type="button" onClick={() => setEditingZip(true)} className={`flex items-center gap-2 border rounded-lg px-3 py-2 w-full transition-colors cursor-pointer text-left ${geoError ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-stone-50 border-stone-200 hover:bg-stone-100'}`} title="Click to change ZIP code" data-id="element-1513">
                            <MapPinIcon className={`w-4 h-4 flex-shrink-0 ${geoError ? 'text-red-500' : 'text-[#8B7D6B]'}`} data-id="element-1514" />
                            <span className={`text-sm font-semibold ${geoError ? 'text-red-700' : 'text-[#8B7D6B]'}`} data-id="element-1515">
                              {formData.City}, {formData.State} {formData.Zip}
                            </span>
                            <span className="text-[10px] text-gray-400 ml-auto" data-id="element-1516">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full hover:bg-gray-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-1517">
                            <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-1518" />
                            <span className="text-sm text-gray-500" data-id="element-1519">
                              {formData.Zip ? `Zip: ${formData.Zip}` : 'Enter your ZIP code'}
                            </span>
                          </button>}

                        {geoError && <div className="flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg border border-red-100 mt-2" data-id="element-1520">
                            <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1521" />
                            <p className="text-sm font-medium" data-id="element-1522">{geoError}</p>
                          </div>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.Address)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.Address || !formData.City || !formData.State || !formData.Zip || !!addressError || !!geoError} className="w-full mt-6 h-14 bg-[#8B7D6B] hover:bg-[#5C5145] text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1523">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 2: Contact Information */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1524">
                  <div className="flex justify-center mb-6" data-id="element-1525">
                    <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center" data-id="element-1526">
                      <UserIcon className="w-8 h-8 text-[#8B7D6B]" data-id="element-1527" />
                    </div>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-[#8B7D6B] mb-2 text-center" data-id="element-1528">
                    Who should we contact?
                  </h1>
                  <p className="text-gray-500 text-center mb-8" data-id="element-1529">
                    Your free estimate is almost ready.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1530">
                    <div className="grid grid-cols-2 gap-4" data-id="element-1531">
                      <div data-id="element-1532">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1533">
                          First Name
                        </label>
                        <input type="text" name="FirstName" value={formData.FirstName} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#8B7D6B] focus:border-transparent" required data-id="element-1534" />
                      </div>
                      <div data-id="element-1535">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1536">
                          Last Name
                        </label>
                        <input type="text" name="LastName" value={formData.LastName} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#8B7D6B] focus:border-transparent" required data-id="element-1537" />
                      </div>
                    </div>

                    <div data-id="element-1538">
                      <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1539">
                        Phone Number
                      </label>
                      <input type="tel" name="PhoneNumber" value={formData.PhoneNumber} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#8B7D6B] focus:border-transparent" placeholder="(555) 555-5555" required maxLength={10} data-id="element-1540" />
                    </div>

                    <div data-id="element-1541">
                      <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1542">
                        Email Address
                      </label>
                      <input type="email" name="Email" value={formData.Email} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#8B7D6B] focus:border-transparent" required data-id="element-1543" />
                    </div>

                    <div className="mt-6" data-id="element-1544">
                      <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1545">
                        <div className="relative flex items-start pt-1" data-id="element-1546">
                          <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="peer sr-only" data-id="element-1547" />
                          <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#8B7D6B] peer-checked:border-[#8B7D6B] transition-colors flex items-center justify-center" data-id="element-1548">
                            <CheckCircle2Icon className="w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100" data-id="element-1549" />
                          </div>
                        </div>
                        <span className="text-[11px] leading-tight text-gray-500 select-none" data-id="element-1550">
                          By clicking "Get My Free Quote", I agree to the Terms
                          of Service and Privacy Policy. I authorize Floor
                          Coverings International and its partners to contact me
                          at the phone number and email provided, including via
                          automated technology, text messages, and prerecorded
                          voice. Consent is not a condition of purchase.
                        </span>
                      </label>
                    </div>

                    <button type="submit" disabled={isSubmitting || !formData.FirstName || !formData.LastName || formData.PhoneNumber.length < 10 || !formData.Email || !consentChecked} className="w-full mt-6 h-16 bg-[#FF6F00] hover:bg-orange-700 text-white font-bold text-xl rounded-xl transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2" data-id="element-1551">
                      {isSubmitting ? <>
                          <LoaderIcon className="w-6 h-6 animate-spin" data-id="element-1552" />
                          Submitting...
                        </> : 'Get My Free Quote'}
                    </button>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}